import React, { createContext, useCallback, useEffect, useReducer, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project-imports
import Loader from 'components/Loader';
import { apiClient } from 'api/vektra/client';
import { setTrackedUser } from 'errorTracking';
import { setAxiosOnUnauthorized, setAxiosTokenRefresher } from 'utils/axios';
import {
  login as apiLogin,
  refreshToken as apiRefreshToken,
  selectAccount as apiSelectAccount,
  switchAccount as apiSwitchAccount,
  targetAccount as apiTargetAccount,
  getAccounts as apiGetAccounts,
  logout as apiLogout,
  verifyOtp as apiVerifyOtp,
  setPassword as apiSetPassword,
  impersonate as apiImpersonate
} from 'api/vektra/auth';
import { unregisterOnLogout } from 'api/vektra/push';
import { syncPreferredLanguage } from 'api/vektra/language';
import i18n from 'i18n';
import type {
  AuthResponse,
  UserResponse,
  LoginRequest,
  SelectAccountRequest,
  VerifyOtpRequest,
  SetPasswordRequest,
  ImpersonateRequest,
  AccountMembershipResponse
} from 'api/vektra/types';

// types
import { AuthProps } from 'types/auth';
import { authStorage, IMPERSONATION_FLAG, IMPERSONATION_HANDOFF_PREFIX, IMPERSONATION_HANDOFF_PARAM } from 'utils/impersonation';

export const ACCESS_TOKEN_KEY = 'vektra_access_token';
export const REFRESH_TOKEN_KEY = 'vektra_refresh_token';
export const USER_KEY = 'vektra_user';

interface VektraExtraState {
  requiresAccountSelection: boolean;
  requiresPasswordChange: boolean;
  requiresOtpVerification: boolean;
  otpEmail: string | null;
  accounts: AccountMembershipResponse[];
}

export interface VektraJWTContextType extends AuthProps, VektraExtraState {
  login: (email: string, password: string) => Promise<AuthResponse>;
  loginRequest: (data: LoginRequest) => Promise<AuthResponse>;
  verifyOtp: (data: VerifyOtpRequest) => Promise<AuthResponse>;
  setPassword: (data: SetPasswordRequest) => Promise<AuthResponse>;
  startOtpFlow: (email: string) => void;
  fetchAccounts: () => Promise<AccountMembershipResponse[]>;
  selectAccount: (data: SelectAccountRequest) => Promise<AuthResponse>;
  switchAccount: (businessAccountId: string) => Promise<void>;
  selectTargetAccount: (businessAccountId: string) => Promise<AuthResponse>;
  switchToTargetAccount: (businessAccountId: string) => Promise<void>;
  impersonate: (data: ImpersonateRequest) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  // No-ops kept for compatibility with Able Pro's existing screens.
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /** USER-03 (VEK-256) — after PUT /users/me: the returned user replaces the login snapshot, so the header follows. */
  updateProfile: (user: UserResponse) => void;
}

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const JWTContext = createContext<VektraJWTContextType | null>(null);

/** The business account of the user saved with the tab's session, if any. */
function storedBusinessAccountId(): string | undefined {
  try {
    const raw = authStorage().getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as UserResponse).businessAccountId || undefined : undefined;
  } catch {
    return undefined;
  }
}

function userToProfile(user: UserResponse) {
  return {
    id: user.id,
    email: user.email,
    name: `${user.firstName} ${user.lastName}`.trim(),
    role: user.role,
    businessAccountId: user.businessAccountId
  };
}

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [extra, setExtra] = React.useState<VektraExtraState>({
    requiresAccountSelection: false,
    requiresPasswordChange: false,
    requiresOtpVerification: false,
    otpEmail: null,
    accounts: []
  });

  const queryClient = useQueryClient();

  // Error tracking identity (OBS-01): the signed-in user's id and tenant, never the email. Keyed on
  // reducer state so every path that signs in or out — login, restore, account switch,
  // impersonation, each LOGOUT — is covered by this one effect.
  const trackedUserId = state.isLoggedIn ? state.user?.id : undefined;
  const trackedAccountId = state.isLoggedIn ? state.user?.businessAccountId : undefined;
  useEffect(() => {
    setTrackedUser(trackedUserId ? { id: trackedUserId, businessAccountId: trackedAccountId } : null);
  }, [trackedUserId, trackedAccountId]);
  const refreshTokenRef = useRef<string | null>(null);
  const handlingUnauthorizedRef = useRef(false);

  // VEK-45: one refresh POST in flight per tab. The refresh token is one-time-use, and the apiClient
  // and axios/SWR each coalesce their own 401s without knowing about the other — a second POST with
  // the same token loses the rotation to the first and signs the user out of the session the first
  // one just renewed. Every refresh in this file joins this promise instead of posting its own.
  const refreshInFlightRef = useRef<Promise<AuthResponse> | null>(null);
  const refreshOnce = useCallback((refreshToken: string): Promise<AuthResponse> => {
    if (!refreshInFlightRef.current) {
      // VEK-60: name the active business account, or the API re-issues the session into the user's
      // first membership — undoing an account switch at the next refresh.
      const businessAccountId = storedBusinessAccountId();
      refreshInFlightRef.current = apiRefreshToken({ refreshToken, businessAccountId }).finally(() => {
        refreshInFlightRef.current = null;
      });
    }
    return refreshInFlightRef.current;
  }, []);

  const clearTokens = useCallback(() => {
    const store = authStorage();
    store.removeItem(ACCESS_TOKEN_KEY);
    store.removeItem(REFRESH_TOKEN_KEY);
    store.removeItem(USER_KEY);
    // Drop the impersonation marker so a cleared tab reverts to a normal session.
    try {
      sessionStorage.removeItem(IMPERSONATION_FLAG);
    } catch {
      // sessionStorage may be unavailable; ignore.
    }
    apiClient.setToken(null);
    refreshTokenRef.current = null;
  }, []);

  const storeTokens = useCallback((response: AuthResponse) => {
    const store = authStorage();
    if (response.accessToken) {
      store.setItem(ACCESS_TOKEN_KEY, response.accessToken);
      apiClient.setToken(response.accessToken);
    }
    if (response.refreshToken) {
      store.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
      refreshTokenRef.current = response.refreshToken;
    }
    if (response.user) {
      store.setItem(USER_KEY, JSON.stringify(response.user));
    }
  }, []);

  const handleUnauthorized = useCallback(() => {
    if (handlingUnauthorizedRef.current) return;
    handlingUnauthorizedRef.current = true;
    clearTokens();
    queryClient.clear();
    dispatch({ type: LOGOUT });
    setExtra({
      requiresAccountSelection: false,
      requiresPasswordChange: false,
      requiresOtpVerification: false,
      otpEmail: null,
      accounts: []
    });
    setTimeout(() => {
      handlingUnauthorizedRef.current = false;
    }, 2000);
  }, [clearTokens, queryClient]);

  useEffect(() => {
    const refresher = async () => {
      const stored = refreshTokenRef.current || authStorage().getItem(REFRESH_TOKEN_KEY);
      if (!stored) {
        handleUnauthorized();
        return null;
      }
      let response: AuthResponse;
      try {
        response = await refreshOnce(stored);
      } catch (error) {
        // VEK-57: a fetch that never got a response (offline, a Safari tab resuming, DNS) rejects
        // with TypeError. Nothing rejected the session, so keep the tokens and fail only the
        // request in hand — returning null here would make the client sign the user out.
        if (error instanceof TypeError) throw error;
        handleUnauthorized();
        return null;
      }
      try {
        storeTokens(response);
        return response.accessToken;
      } catch {
        handleUnauthorized();
        return null;
      }
    };

    apiClient.setOnUnauthorized(() => handleUnauthorized());
    setAxiosOnUnauthorized(() => handleUnauthorized());
    apiClient.setTokenRefresher(refresher);
    setAxiosTokenRefresher(refresher);
    return () => {
      apiClient.setTokenRefresher(null);
      apiClient.setOnUnauthorized(null);
      setAxiosOnUnauthorized(null);
      setAxiosTokenRefresher(null);
    };
  }, [handleUnauthorized, storeTokens, refreshOnce]);

  const handleFullAuth = useCallback(
    (response: AuthResponse) => {
      storeTokens(response);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: userToProfile(response.user)
        }
      });
      // NOTIF-07 (VEK-168) — the server learns the language this session reads the app in.
      syncPreferredLanguage(i18n.language);
      setExtra({
        requiresAccountSelection: false,
        requiresPasswordChange: false,
        requiresOtpVerification: false,
        otpEmail: null,
        accounts: response.accounts || []
      });
      return response;
    },
    [storeTokens]
  );

  const handleLoginResponse = useCallback(
    (response: AuthResponse) => {
      storeTokens(response);
      dispatch({
        type: LOGOUT
      });
      setExtra({
        requiresAccountSelection: true,
        requiresPasswordChange: false,
        requiresOtpVerification: false,
        otpEmail: null,
        accounts: response.accounts || []
      });
      return response;
    },
    [storeTokens]
  );

  // Adopt an impersonation hand-off if this tab was opened to carry one.
  // Returns true when a session was adopted (caller should skip normal restore).
  const tryAdoptImpersonationHandoff = useCallback((): boolean => {
    let handoffId: string | null = null;
    try {
      handoffId = new URLSearchParams(window.location.search).get(IMPERSONATION_HANDOFF_PARAM);
    } catch {
      return false;
    }
    if (!handoffId) return false;

    const key = IMPERSONATION_HANDOFF_PREFIX + handoffId;
    const raw = localStorage.getItem(key);
    // Single-use: remove immediately so a refresh can't replay it.
    localStorage.removeItem(key);
    if (!raw) return false;

    try {
      const response: AuthResponse = JSON.parse(raw);
      // Mark the tab before storing so authStorage() points at sessionStorage.
      sessionStorage.setItem(IMPERSONATION_FLAG, 'true');
      handleFullAuth(response);
      return true;
    } catch {
      sessionStorage.removeItem(IMPERSONATION_FLAG);
      return false;
    }
  }, [handleFullAuth]);

  // Restore session on mount
  useEffect(() => {
    (async () => {
      try {
        if (tryAdoptImpersonationHandoff()) return;

        const store = authStorage();
        const accessToken = store.getItem(ACCESS_TOKEN_KEY);
        const refreshTokenValue = store.getItem(REFRESH_TOKEN_KEY);
        const userJson = store.getItem(USER_KEY);

        if (refreshTokenValue) {
          refreshTokenRef.current = refreshTokenValue;
        }

        if (accessToken && userJson) {
          apiClient.setToken(accessToken);
          const user: UserResponse = JSON.parse(userJson);
          if (user.businessAccountId) {
            dispatch({
              type: LOGIN,
              payload: { isLoggedIn: true, user: userToProfile(user) }
            });
            setExtra((prev) => ({
              ...prev,
              accounts: user.memberships || []
            }));
          } else {
            dispatch({ type: LOGOUT });
            setExtra((prev) => ({ ...prev, requiresAccountSelection: true }));
          }
        } else if (refreshTokenValue) {
          let response: AuthResponse | null = null;
          try {
            response = await refreshOnce(refreshTokenValue);
          } catch (error) {
            // VEK-57: a network failure is not a rejected session — show the login page but keep
            // the refresh token, so the next load after the network returns restores the session.
            if (!(error instanceof TypeError)) clearTokens();
            dispatch({ type: LOGOUT });
          }
          if (response) {
            try {
              handleFullAuth(response);
            } catch {
              clearTokens();
              dispatch({ type: LOGOUT });
            }
          }
        } else {
          dispatch({ type: LOGOUT });
        }
      } catch {
        dispatch({ type: LOGOUT });
      }
    })();
  }, [handleFullAuth, clearTokens, tryAdoptImpersonationHandoff, refreshOnce]);

  const loginRequest = useCallback(
    async (data: LoginRequest) => {
      const response = await apiLogin(data);
      return handleLoginResponse(response);
    },
    [handleLoginResponse]
  );

  const login = useCallback(async (email: string, password: string) => loginRequest({ email, password }), [loginRequest]);

  const startOtpFlow = useCallback((email: string) => {
    setExtra((prev) => ({ ...prev, requiresOtpVerification: true, otpEmail: email }));
  }, []);

  const verifyOtp = useCallback(
    async (data: VerifyOtpRequest) => {
      const response = await apiVerifyOtp(data);
      storeTokens(response);
      setExtra((prev) => ({
        ...prev,
        requiresOtpVerification: false,
        requiresPasswordChange: true,
        otpEmail: null
      }));
      return response;
    },
    [storeTokens]
  );

  const setPasswordFn = useCallback(
    async (data: SetPasswordRequest) => {
      const response = await apiSetPassword(data);
      storeTokens(response);
      dispatch({ type: LOGOUT });
      setExtra({
        requiresAccountSelection: true,
        requiresPasswordChange: false,
        requiresOtpVerification: false,
        otpEmail: null,
        accounts: response.accounts || []
      });
      return response;
    },
    [storeTokens]
  );

  const fetchAccounts = useCallback(async () => {
    const accounts = await apiGetAccounts();
    setExtra((prev) => ({ ...prev, accounts }));
    return accounts;
  }, []);

  const selectAccount = useCallback(
    async (data: SelectAccountRequest) => {
      const response = await apiSelectAccount(data);
      return handleFullAuth(response);
    },
    [handleFullAuth]
  );

  // VEK-60: re-sign this tab into another of the user's business accounts. The full page load drops
  // everything held in memory for the old account and restores the new session from storage.
  const switchAccount = useCallback(
    async (businessAccountId: string) => {
      const response = await apiSwitchAccount({ businessAccountId });
      storeTokens(response);
      queryClient.clear();
      window.location.assign('/home');
    },
    [storeTokens, queryClient]
  );

  // VEK-144 (ACCT-02): a target link at sign-in — the same as choosing that account's card.
  const selectTargetAccount = useCallback(
    async (businessAccountId: string) => {
      const response = await apiTargetAccount({ businessAccountId });
      return handleFullAuth(response);
    },
    [handleFullAuth]
  );

  // VEK-144 (ACCT-02): a target link opened in a signed-in tab — re-signs it as switchAccount does.
  const switchToTargetAccount = useCallback(
    async (businessAccountId: string) => {
      const response = await apiTargetAccount({ businessAccountId });
      storeTokens(response);
      queryClient.clear();
      window.location.assign('/home');
    },
    [storeTokens, queryClient]
  );

  const impersonate = useCallback(
    async (data: ImpersonateRequest) => {
      const response = await apiImpersonate(data);
      queryClient.clear();
      return handleFullAuth(response);
    },
    [handleFullAuth, queryClient]
  );

  const logout = useCallback(async () => {
    // PUSH-01 — drop this browser's push subscription while the session can still authorize it, so the
    // next person signed in here never receives this user's pushes. Bounded and never throws.
    await unregisterOnLogout();
    const stored = refreshTokenRef.current || localStorage.getItem(REFRESH_TOKEN_KEY);
    try {
      await apiLogout(stored);
    } catch {
      // Logout client-side even if server call fails.
    }
    clearTokens();
    queryClient.clear();
    dispatch({ type: LOGOUT });
    setExtra({
      requiresAccountSelection: false,
      requiresPasswordChange: false,
      requiresOtpVerification: false,
      otpEmail: null,
      accounts: []
    });
  }, [clearTokens, queryClient]);

  const register = useCallback(async () => {
    // Self-service registration is not supported in Vektra; users are invited.
    throw new Error('Registration is not available — please contact your administrator.');
  }, []);

  const resetPassword = useCallback(async (_email: string) => {
    // Password reset goes through the OTP flow on the login screen.
  }, []);

  // USER-03 (VEK-256) — the header name was a login snapshot (`vektra_user`); a profile save hands the
  // returned user here, which re-stores it and re-dispatches LOGIN so every consumer re-renders.
  const updateProfile = useCallback((user: UserResponse) => {
    authStorage().setItem(USER_KEY, JSON.stringify(user));
    dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: userToProfile(user) } });
  }, []);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext
      value={{
        ...state,
        ...extra,
        login,
        loginRequest,
        verifyOtp,
        setPassword: setPasswordFn,
        startOtpFlow,
        fetchAccounts,
        selectAccount,
        switchAccount,
        selectTargetAccount,
        switchToTargetAccount,
        impersonate,
        logout,
        register,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </JWTContext>
  );
};

export default JWTContext;
